import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert,
  Badge,
  ListGroup,
  ListGroupItem,
} from "reactstrap";
import Swal from "sweetalert2";
import Select from "react-select";
import {
  updateDashboardUserUpline,
  convertGAToNational,
  getRolesAdmin,
} from "../../../slices/admin/thunk";
import { DASHBOARD_USER_ROLES } from "../../../constants";

// Extract the user array out of the various admin-list response shapes.
const extractUsers = (response) => {
  if (response?.users) return response.users;
  if (response?.data?.users) return response.data.users;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
};

const optionLabel = (u) =>
  `${u.firstName || ""} ${u.lastName || ""}`.trim() +
  (u.inviteCode ? ` (${u.inviteCode})` : "") +
  (u.email ? ` — ${u.email}` : "");

// Searchable Select of ALL possible uplines for a role. Loads every option up
// front (paginating the admin list) and lets react-select filter as you type.
const UserSearchSelector = ({
  role,
  label,
  selectedUser,
  onSelectUser,
  onClear,
  dispatch,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadAll = async () => {
      setLoading(true);
      try {
        const byId = new Map();
        let page = 0;
        // Page through until no new users are returned (also stops cleanly if
        // the endpoint ignores pagination and returns everything at once).
        for (let i = 0; i < 50; i++) {
          const response = await dispatch(
            getRolesAdmin({ role, status: undefined, page, query: "" })
          ).unwrap();
          const users = extractUsers(response);
          const before = byId.size;
          users.forEach((u) => {
            if (u && u.id) byId.set(u.id, u);
          });
          if (byId.size === before) break;
          page += 1;
        }
        if (cancelled) return;
        const all = Array.from(byId.values());
        all.sort((a, b) =>
          optionLabel(a).localeCompare(optionLabel(b))
        );
        setOptions(
          all.map((u) => ({ value: u.id, label: optionLabel(u), user: u }))
        );
      } catch (error) {
        console.error(`Error loading ${role} options:`, error);
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAll();
    return () => {
      cancelled = true;
    };
  }, [role, dispatch]);

  const selectedValue = selectedUser
    ? {
        value: selectedUser.id,
        label: optionLabel(selectedUser),
        user: selectedUser,
      }
    : null;

  return (
    <FormGroup>
      <Label>{label}</Label>
      <Select
        isClearable
        isLoading={loading}
        options={options}
        value={selectedValue}
        onChange={(opt) => (opt ? onSelectUser(opt.user) : onClear())}
        placeholder={loading ? "Loading…" : `Select a ${label}…`}
        noOptionsMessage={() => (loading ? "Loading…" : "No options")}
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : undefined
        }
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
      <small className="text-muted">
        Pick a {label} from the list (searchable). Leave empty to remove
        assignment.
      </small>
    </FormGroup>
  );
};

const ManageUplineModal = ({ isOpen, setIsOpen, userProfile, onRefresh }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Selected users
  const [selectedNational, setSelectedNational] = useState(null);
  const [selectedGA, setSelectedGA] = useState(null);
  const [selectedBroker, setSelectedBroker] = useState(null);

  const userRole = userProfile?.role;

  // Determine what fields to show based on role
  const canEditNational = [DASHBOARD_USER_ROLES.GA, DASHBOARD_USER_ROLES.BROKER].includes(userRole);
  const canEditGA = [DASHBOARD_USER_ROLES.BROKER].includes(userRole);
  const canEditBroker = [DASHBOARD_USER_ROLES.EMPLOYER].includes(userRole);
  const canConvertToNational = userRole === DASHBOARD_USER_ROLES.GA;

  const resetModal = () => {
    setSelectedNational(null);
    setSelectedGA(null);
    setSelectedBroker(null);
  };

  const handleClose = () => {
    resetModal();
    setIsOpen(false);
  };

  // Initialize form with current values
  useEffect(() => {
    if (isOpen && userProfile) {
      // Set selected users from current upline info
      if (userProfile.nationalInfo) {
        setSelectedNational({
          id: userProfile.nationalId,
          firstName: userProfile.nationalInfo.firstName,
          lastName: userProfile.nationalInfo.lastName,
          inviteCode: userProfile.nationalInfo.inviteCode,
        });
      } else {
        setSelectedNational(null);
      }

      if (userProfile.gaInfo) {
        setSelectedGA({
          id: userProfile.gaId,
          firstName: userProfile.gaInfo.firstName,
          lastName: userProfile.gaInfo.lastName,
          inviteCode: userProfile.gaInfo.inviteCode,
        });
      } else {
        setSelectedGA(null);
      }

      if (userProfile.brokerInfo) {
        setSelectedBroker({
          id: userProfile.brokerId,
          firstName: userProfile.brokerInfo.firstName,
          lastName: userProfile.brokerInfo.lastName,
          inviteCode: userProfile.brokerInfo.inviteCode,
        });
      } else {
        setSelectedBroker(null);
      }
    }
  }, [isOpen, userProfile]);

  const handleSave = async () => {
    try {
      setLoading(true);

      // Build update payload based on role
      const payload = { userId: userProfile.id };

      if (canEditNational) {
        payload.nationalId = selectedNational?.id || null;
      }
      if (canEditGA) {
        payload.gaId = selectedGA?.id || null;
      }
      if (canEditBroker) {
        payload.brokerId = selectedBroker?.id || null;
      }

      const response = await dispatch(updateDashboardUserUpline(payload)).unwrap();

      Swal.fire({
        title: "Success!",
        text: response.message || "Upline updated successfully",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      if (onRefresh) {
        await onRefresh();
      }

      handleClose();
    } catch (error) {
      console.error("Error updating upline:", error);
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to update upline",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToNational = async () => {
    try {
      const result = await Swal.fire({
        title: "Convert GA to National?",
        html: `
          <p>This will:</p>
          <ul style="text-align: left;">
            <li>Change <strong>${userProfile.firstName} ${userProfile.lastName}</strong>'s role to National</li>
            <li>Update all brokers under this GA to report directly to the new National</li>
            <li>Update all employers under those brokers</li>
          </ul>
          <p><strong>This action cannot be undone.</strong></p>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, convert to National",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
      });

      if (!result.isConfirmed) return;

      Swal.fire({
        title: "Converting...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await dispatch(
        convertGAToNational({ userId: userProfile.id })
      ).unwrap();

      Swal.fire({
        title: "Success!",
        text: response.message || "GA successfully converted to National",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      if (onRefresh) {
        await onRefresh();
      }

      handleClose();
    } catch (error) {
      console.error("Error converting to National:", error);
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to convert GA to National",
        icon: "error",
      });
    }
  };

  const renderCurrentUpline = () => {
    const uplineInfo = [];

    if (userProfile?.nationalInfo) {
      uplineInfo.push({
        label: "Current National",
        name: `${userProfile.nationalInfo.firstName} ${userProfile.nationalInfo.lastName}`,
        inviteCode: userProfile.nationalInfo.inviteCode,
      });
    }

    if (userProfile?.gaInfo) {
      uplineInfo.push({
        label: "Current GA",
        name: `${userProfile.gaInfo.firstName} ${userProfile.gaInfo.lastName}`,
        inviteCode: userProfile.gaInfo.inviteCode,
      });
    }

    if (userProfile?.brokerInfo) {
      uplineInfo.push({
        label: "Current Broker",
        name: `${userProfile.brokerInfo.firstName} ${userProfile.brokerInfo.lastName}`,
        inviteCode: userProfile.brokerInfo.inviteCode,
      });
    }

    if (uplineInfo.length === 0) {
      return (
        <Alert color="info">
          <i className="ri-information-line me-2"></i>
          This user currently has no upline assigned.
        </Alert>
      );
    }

    return (
      <div className="mb-3">
        {uplineInfo.map((info, index) => (
          <div key={index} className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <strong>{info.label}:</strong> {info.name}
            </div>
            <Badge color="secondary">{info.inviteCode}</Badge>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        Manage Upline - {userProfile?.firstName} {userProfile?.lastName}
      </ModalHeader>
      <ModalBody>
        {/* Current Upline Info */}
        <Card className="mb-3">
          <CardHeader>
            <h6 className="mb-0">Current Upline</h6>
          </CardHeader>
          <CardBody>{renderCurrentUpline()}</CardBody>
        </Card>

        {/* Edit Upline Form */}
        <Card>
          <CardHeader>
            <h6 className="mb-0">Update Upline</h6>
          </CardHeader>
          <CardBody>
            <Form>
              {/* National Selector */}
              {canEditNational && (
                <UserSearchSelector
                  role="nationals"
                  label="National"
                  selectedUser={selectedNational}
                  onSelectUser={setSelectedNational}
                  onClear={() => setSelectedNational(null)}
                  dispatch={dispatch}
                />
              )}

              {/* GA Selector */}
              {canEditGA && (
                <UserSearchSelector
                  role="ga"
                  label="GA"
                  selectedUser={selectedGA}
                  onSelectUser={setSelectedGA}
                  onClear={() => setSelectedGA(null)}
                  dispatch={dispatch}
                />
              )}

              {/* Broker Selector */}
              {canEditBroker && (
                <UserSearchSelector
                  role="brokers"
                  label="Broker"
                  selectedUser={selectedBroker}
                  onSelectUser={setSelectedBroker}
                  onClear={() => setSelectedBroker(null)}
                  dispatch={dispatch}
                />
              )}

              {!canEditNational && !canEditGA && !canEditBroker && (
                <Alert color="warning">
                  <i className="ri-alert-line me-2"></i>
                  This user's role ({userRole}) does not have an editable upline.
                </Alert>
              )}
            </Form>
          </CardBody>
        </Card>

        {/* Convert GA to National */}
        {canConvertToNational && (
          <Card className="mt-3 border-warning">
            <CardHeader className="bg-warning-subtle">
              <h6 className="mb-0 text-warning">
                <i className="ri-exchange-line me-2"></i>
                Convert to National
              </h6>
            </CardHeader>
            <CardBody>
              <p className="mb-2">
                Convert this GA to a National. This will:
              </p>
              <ul className="mb-3">
                <li>Change their role from GA to National</li>
                <li>Remove their current National assignment</li>
                <li>Update all brokers under this GA to report to the new National</li>
              </ul>
              <Button
                color="warning"
                onClick={handleConvertToNational}
                disabled={loading}
              >
                <i className="ri-exchange-line me-1"></i>
                Convert to National
              </Button>
            </CardBody>
          </Card>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSave}
          disabled={loading || (!canEditNational && !canEditGA && !canEditBroker)}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            <>
              <i className="ri-save-line me-1"></i>
              Save Changes
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ManageUplineModal;
